import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  isValidIndianMobile, isPartialIndianMobile,
  normalizeIndianMobile, phoneErrorMessage,
} from '@/utils/phone';
import { applyPatientToForm, computeDiscPreview } from './walkinFormUtils';
import { submitWalkinCheckin } from './submitWalkinCheckin';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/** Walk-in registration state: lookup, autofill, fee, check-in. */
export default function useWalkinForm({ selectedSession, doctors, onCheckedIn }) {
  const [pPhone, setPPhone] = useState('');
  const [pEmail, setPEmail] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState('new');
  const [selectedExisting, setSelectedExisting] = useState(null);
  const [autofillNote, setAutofillNote] = useState('');
  const [pName, setPName] = useState('');
  const [pAge, setPAge] = useState('');
  const [pGender, setPGender] = useState('male');
  const [pRelationship, setPRelationship] = useState('self');
  const [symptoms, setSymptoms] = useState('');
  const [bookingMode, setBookingMode] = useState('walkin');
  const [slot, setSlot] = useState('');
  const [fee, setFee] = useState('');
  const [feeTier, setFeeTier] = useState(null);
  const [payMethod, setPayMethod] = useState('cash');
  const [payRef, setPayRef] = useState('');
  const [paying, setPaying] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discKind, setDiscKind] = useState('percent');
  const [discValue, setDiscValue] = useState('');
  const [discReason, setDiscReason] = useState('');
  const [discNote, setDiscNote] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [discCfg, setDiscCfg] = useState({ approval_threshold_percent: 50, reasons: [] });

  const setters = useMemo(() => ({
    setPName, setPAge, setPGender, setPEmail, setPPhone,
    setPRelationship, setSelectedExisting, setMode, setAutofillNote,
  }), []);

  useEffect(() => {
    ax.get('/api/clinic-ops/discount/config').then(({ data }) => setDiscCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSession && !fee) setFee(String(selectedSession.consultation_fee || ''));
  }, [selectedSession?.id]); // eslint-disable-line

  useEffect(() => {
    const digits = normalizeIndianMobile(pPhone);
    if (digits.length < 4) return undefined;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await ax.get(`/api/clinic-ops/patients/search?phone=${encodeURIComponent(digits)}`);
        const rows = r.data || [];
        setMatches(rows);
        if (rows.length === 0) {
          setMode('new'); setPRelationship('self'); setSelectedExisting(null); setAutofillNote('');
        } else if (rows.length === 1) {
          applyPatientToForm(rows[0], setters);
          toast.message('Patient found', { description: 'Details filled from records' });
        } else {
          setMode('pick'); setSelectedExisting(null);
          setAutofillNote(`${rows.length} patients share this phone — pick who is visiting`);
        }
      } catch (_) { setMatches([]); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [pPhone]); // eslint-disable-line

  useEffect(() => {
    const em = (pEmail || '').trim().toLowerCase();
    if (!em.includes('@') || em.length < 5) return undefined;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await ax.get(`/api/clinic-ops/patients/search?email=${encodeURIComponent(em)}`);
        const rows = r.data || [];
        if (rows.length === 1) {
          applyPatientToForm(rows[0], setters);
          setMatches(rows);
          toast.message('Patient found by email', { description: 'Details filled from records' });
        } else if (rows.length > 1) {
          setMatches(rows); setMode('pick');
          setAutofillNote(`${rows.length} patients match this email — pick one`);
        }
      } catch (_) { /* ignore */ }
      setSearching(false);
    }, 400);
    return () => clearTimeout(t);
  }, [pEmail]); // eslint-disable-line

  useEffect(() => {
    if (!selectedSession?.doctor_id) { setFeeTier(null); return undefined; }
    const patientId = mode === 'pick' ? selectedExisting?.id : null;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ doctor_id: selectedSession.doctor_id });
        if (patientId) params.set('patient_id', patientId);
        const { data } = await ax.get(`/api/clinic-ops/fee?${params.toString()}`);
        if (cancelled) return;
        setFeeTier(data?.tier || null);
        if (data?.amount != null) setFee(String(data.amount));
      } catch (_) { /* keep */ }
    })();
    return () => { cancelled = true; };
  }, [selectedSession?.doctor_id, mode, selectedExisting?.id]);

  const resetPatientFields = useCallback(() => {
    setPPhone(''); setPEmail(''); setPName(''); setPAge(''); setSlot(''); setSymptoms('');
    setMatches([]); setMode('new'); setPRelationship('self'); setSelectedExisting(null);
    setPhoneTouched(false); setAutofillNote('');
    setShowDiscount(false); setDiscKind('percent'); setDiscValue('');
    setDiscReason(''); setDiscNote(''); setAdminPin(''); setPayRef('');
  }, []);

  const phoneValid = isValidIndianMobile(pPhone);
  const phoneShowError = (phoneTouched && !phoneValid)
    || (pPhone.length > 0 && !isPartialIndianMobile(pPhone));
  const phoneErrMsg = phoneShowError ? phoneErrorMessage(pPhone) : '';
  const feeNum = Math.max(0, parseFloat(fee || 0) || 0);
  const rawDisc = Math.max(0, parseFloat(discValue || 0) || 0);
  const discPreview = computeDiscPreview(
    feeNum, showDiscount, rawDisc, discKind, discCfg?.approval_threshold_percent,
  );
  const docName = (id) => doctors.find((d) => d.id === id)?.name || '—';

  const checkin = async () => {
    setPaying(true);
    try {
      const result = await submitWalkinCheckin({
        selectedSession, mode, selectedExisting, pName, pPhone, pEmail, pAge, pGender,
        pRelationship, phoneValid, setPhoneTouched, bookingMode, slot, symptoms,
        showDiscount, rawDisc, discKind, discReason, discNote, discPreview, adminPin,
        discCfg, payMethod, payRef, feeTier, docName, resetPatientFields,
      });
      if (result?.ok) onCheckedIn?.(result.receipt);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Check-in failed');
    }
    setPaying(false);
  };

  return {
    pPhone, setPPhone, pEmail, setPEmail, phoneTouched, setPhoneTouched,
    phoneShowError, phoneErrMsg, searching, matches, mode, setMode,
    selectedExisting, setSelectedExisting, autofillNote,
    pName, setPName, pAge, setPAge, pGender, setPGender,
    pRelationship, setPRelationship, symptoms, setSymptoms,
    bookingMode, setBookingMode, slot, setSlot,
    fee, setFee, feeTier, feeNum, payMethod, setPayMethod, payRef, setPayRef,
    paying, showDiscount, setShowDiscount, discKind, setDiscKind,
    discValue, setDiscValue, discReason, setDiscReason, discNote, setDiscNote,
    adminPin, setAdminPin, discCfg, discPreview, rawDisc,
    checkin, resetPatientFields, applyPick: (p) => applyPatientToForm(p, setters),
  };
}
